import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./editorHeader.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { PREVIEW_LISTING_URL } from '~/config';
import { WireI18n } from '~/modules/stores/i18nStore';
import { listingStore, listingStoreValidator, selectExtensionListing, updateListingStoreValidator } from '~/modules/stores/listings/listingStore';
import { pricingStore, shouldTriggerChangeCaseAction } from '~/modules/stores/pricingStore';
import { WirePublisherStore, publisherStore, selectForcePublisher } from '~/modules/stores/publisher/publisherStore';
import { LISTING_EDITOR_ROUTE, navigate } from '~/modules/stores/routerStore';
import { images } from '~/modules/utilities/assetUtilities';
import { upsertQueryParams } from '~/modules/utilities/routingUtilities';
import { TECH_TYPE_ICON_MAP, isConsultantType } from '~/modules/utilities/schemaUtilities';
const HEADER_SCROLL_Y_111 = 111;
const HEADER_SCROLL_Y_90 = 90;
class EditorHeader extends LightningElement {
  constructor(...args) {
    super(...args);
    this.images = images;
    this.shrinkHeader = false;
    this.isLoading = void 0;
    this.uploadingAssets = void 0;
    this.isSaving = void 0;
    this.listing = void 0;
    this.listingExtension = void 0;
    this.percentageCompleted = 0;
    this.isConsultant = void 0;
    this.isJpListing = void 0;
    this.showMaintenanceBanner = void 0;
    this.labels = {};
    this.sharedLabels = {};
    this.listingTypes = {};
    this.solutionTypes = {};
    this.approvalLabels = {};
    this.handlePreview = async event => {
      event.preventDefault();
      const result = await listingStore.saveListing({
        redirectAfterSave: false
      });
      if (result && this.listing.id) {
        window.open(`${PREVIEW_LISTING_URL}${this.listing.external_id}`, '_blank');
        navigate(`/${LISTING_EDITOR_ROUTE}?${upsertQueryParams(['id', this.listing.id], ['preview', 'true'])}`);
      }
    };
    this.updateHeader = () => {
      let posY = window.scrollY;
      const staticProgress = this.template.querySelector('.static-progress');
      const floatingProgress = this.template.querySelector('.floating-progress');
      const listingIcon = this.template.querySelector('.listing-icon');
      if (posY >= HEADER_SCROLL_Y_111) {
        if (staticProgress) {
          staticProgress.classList.remove('hidden');
        }
        if (floatingProgress) {
          floatingProgress.classList.add('hidden');
        }
        if (listingIcon) {
          listingIcon.style.transform = 'translate(-111px, 0px)';
        }
      } else {
        if (staticProgress) {
          staticProgress.classList.add('hidden');
        }
        if (floatingProgress) {
          floatingProgress.classList.remove('hidden');
        }
        if (listingIcon) {
          if (posY > HEADER_SCROLL_Y_90) {
            posY = HEADER_SCROLL_Y_111;
          }
          posY = posY * -1;
          listingIcon.style.transform = 'translate(' + posY + 'px, 0px)';
        }
      }
    };
  }
  listingStoreCallback({
    isLoading,
    isSaving,
    uploadingAssets,
    listing
  }) {
    this.isLoading = isLoading;
    this.uploadingAssets = uploadingAssets;
    this.isSaving = isSaving;
    this.listing = listing;
    this.listingExtension = selectExtensionListing(listing);
    this.updatePercentageCompleted();
  }
  pricingStoreConnected() {
    this.updatePercentageCompleted();
  }
  publisherAgreementStatus() {
    updateListingStoreValidator();
    this.updatePercentageCompleted();
  }
  i18nCallback(i18n) {
    this.labels = i18n.editorHeader;
    this.sharedLabels = i18n.shared;
    this.listingTypes = i18n.listingTypes;
    this.solutionTypes = i18n.solutionTypes;
    this.approvalLabels = i18n.pricing.approvals.modalResubmit;
  }
  get name() {
    return this.listing.name;
  }
  get isSaveDisabled() {
    return this.isSaving || this.uploadingAssets > 0;
  }
  get saveButtonLabel() {
    return this.isSaving ? this.labels.savingButtonLabel : this.labels.saveAndExitButtonLabel;
  }
  get listingTypeName() {
    return this.listingTypes[this.listing.technology];
  }
  get solutionTypeName() {
    return this.solutionTypes[this.listing.technology];
  }
  get iconTypeClasses() {
    return 'icon-type ' + TECH_TYPE_ICON_MAP[this.listing.technology];
  }
  get wayfinderClasses() {
    if (this.showMaintenanceBanner) {
      return 'wayfinder ' + TECH_TYPE_ICON_MAP[this.listing.technology] + ' wayfinder-maintenance';
    }
    return 'wayfinder ' + TECH_TYPE_ICON_MAP[this.listing.technology];
  }
  get headerTitleLabel() {
    const percentage = this.percentageCompleted;
    if (this.listing.public) {
      return this.labels.titlePublished;
    } else if (percentage === 0) {
      return this.labels.titleNotStarted;
    } else if (percentage > 0 && percentage <= 100) {
      return this.labels.titleUpTo99;
    }
    return '';
  }
  get headerSubtitleLabel() {
    const percentage = this.percentageCompleted;
    if (this.listing.public) {
      return this.labels.subtitlePublished;
    } else if (percentage === 0) {
      return this.labels.subtitleNotStarted;
    } else if (percentage > 0 && percentage <= 100) {
      return this.labels.subtitleUpTo99;
    }
    return '';
  }
  get iconTemplate() {
    return TECH_TYPE_ICON_MAP[this.listing.technology];
  }
  get previewButton() {
    return {
      onclick: this.handlePreview,
      label: this.labels.previewButtonLabel,
      iconName: this.isSaving ? 'utility:spinner' : undefined,
      variant: 'brand',
      class: 'small-button slds-m-left_large',
      disabled: this.isSaveDisabled
    };
  }
  get saveButton() {
    return {
      onclick: this.handleSaveAndExit,
      label: this.saveButtonLabel,
      iconName: this.isSaving ? 'utility:spinner' : undefined,
      variant: 'neutral',
      class: 'small-button',
      disabled: this.isSaveDisabled
    };
  }
  get showPreview() {
    return !isConsultantType(this.listing.technology);
  }
  connectedCallback() {
    window.addEventListener('scroll', this.updateHeader, false);
    this.updateHeader();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateHeader);
  }
  handleSaveAndExit(event) {
    event.preventDefault();
    if (shouldTriggerChangeCaseAction()) {
      this.handleOpenResubmissionModal();
    } else {
      listingStore.saveListing();
    }
  }
  handleOpenResubmissionModal() {
    const modal = this.template.querySelector('.submit-modal');
    modal.show();
  }
  handleSaveAndReSubmit(event) {
    event.stopPropagation();
    listingStore.saveListing({
      isSubmitForApproval: true
    });
  }
  handleDialogClose() {
    const modal = this.template.querySelector('.submit-modal');
    modal.hide();
  }
  updatePercentageCompleted() {
    if (!this.listing) {
      this.percentageCompleted = 0;
      return;
    }
    if (this.listing.public) {
      this.percentageCompleted = 100;
      return;
    }
    const {
      stepsCompleted,
      numberOfSteps
    } = listingStoreValidator;
    if (stepsCompleted.length > 0 && numberOfSteps) {
      this.percentageCompleted = Math.floor(stepsCompleted.length * 100 / numberOfSteps);
      return;
    }
    this.percentageCompleted = 0;
  }
  /*LWC compiler v7.1.3*/
}
EditorHeader.delegatesFocus = true;
_registerDecorators(EditorHeader, {
  publicProps: {
    showMaintenanceBanner: {
      config: 0
    }
  },
  wire: {
    listingStoreCallback: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: listingStore,
          selector: ({
            isLoading,
            isSaving,
            uploadingAssets,
            listing
          }) => ({
            isLoading,
            isSaving,
            uploadingAssets,
            listing
          }),
          equalityFn
        };
      }
    },
    pricingStoreConnected: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: pricingStore
        };
      }
    },
    publisherAgreementStatus: {
      adapter: WirePublisherStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: publisherStore,
          selector: data => {
            const {
              ISVClickthroughName
            } = selectForcePublisher(data);
            return ISVClickthroughName;
          }
        };
      }
    },
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["images", "shrinkHeader", "isLoading", "uploadingAssets", "isSaving", "listing", "listingExtension", "percentageCompleted", "isConsultant", "isJpListing", "labels", "sharedLabels", "listingTypes", "solutionTypes", "approvalLabels", "handlePreview", "updateHeader"]
});
const __lwc_component_class_internal = _registerComponent(EditorHeader, {
  tmpl: _tmpl,
  sel: "appx-editor-header",
  apiVersion: 62
});
export default __lwc_component_class_internal;